import type { SkuPriceOptionsResponse, SkuPricesResponse } from '@/types/interfaces/sku'

/** Matches Nile-Core `Sku::TYPICAL_COPAY` (cents) when the prices payload has no insurance amount. */
export const TYPICAL_VISIT_COPAY_CENTS = 2500

/** Footer label after the dollar amount, matching PDP visit-cost design copy. */
export type MedicationOptionVisitCostLabel = 'typical copay' | 'est. insurance price' | 'est. price'

export type MedicationOptionVisitCost = {
    amountCents: number
    label: MedicationOptionVisitCostLabel
}

/**
 * Visit cost for a medication-option PDP footer (not pharmacy fill price).
 *
 * Unauthenticated: always show typical copay. Prefer `prices.insurance.price`
 * (backend Sku::TYPICAL_COPAY), then option-level insurance/typical_copay, then
 * {@link TYPICAL_VISIT_COPAY_CENTS}. Never use cash for logged-out visitors.
 * Authenticated: primary cash/insurance path, else the lower of the two.
 */
export function resolveMedicationOptionVisitCost(
    prices: SkuPricesResponse | null | undefined,
    authenticated: boolean,
): MedicationOptionVisitCost | null {
    if (!authenticated) {
        return {
            amountCents: resolveInsuranceOrTypicalCopayCents(prices) ?? TYPICAL_VISIT_COPAY_CENTS,
            label: 'typical copay',
        }
    }

    if (!prices) return null

    const cashAmount = prices.cash?.price ?? null
    const insuranceAmount = resolveInsuranceOrTypicalCopayCents(prices)

    if (prices.cash?.primary === true && cashAmount != null) {
        return { amountCents: cashAmount, label: 'est. price' }
    }
    if (prices.insurance?.primary === true && insuranceAmount != null) {
        return { amountCents: insuranceAmount, label: 'est. insurance price' }
    }

    if (cashAmount != null && insuranceAmount != null) {
        return cashAmount <= insuranceAmount
            ? { amountCents: cashAmount, label: 'est. price' }
            : { amountCents: insuranceAmount, label: 'est. insurance price' }
    }
    if (cashAmount != null) return { amountCents: cashAmount, label: 'est. price' }
    if (insuranceAmount != null) return { amountCents: insuranceAmount, label: 'est. insurance price' }
    return null
}

/** Prefer top-level insurance.price; else min of per-option insurance / typical_copay. */
function resolveInsuranceOrTypicalCopayCents(prices: SkuPricesResponse | null | undefined): number | null {
    if (!prices) return null
    if (prices.insurance?.price != null) return prices.insurance.price

    const fromOptions = (prices.options ?? [])
        .flatMap((option) => [option.insurance, option.typical_copay])
        .filter((amount): amount is number => typeof amount === 'number')

    if (fromOptions.length === 0) return null
    return Math.min(...fromOptions)
}

/** Normalize `sku_price_mapping[].prices`, which may be a single object or a one-element array. */
export function skuPricesForId(
    response: SkuPriceOptionsResponse | null | undefined,
    skuId: string | number,
): SkuPricesResponse | null {
    const pricesEntry = response?.sku_price_mapping?.find((mapping) => String(mapping.id) === String(skuId))?.prices
    if (!pricesEntry) return null
    return Array.isArray(pricesEntry) ? (pricesEntry[0] ?? null) : pricesEntry
}
